import {
  action,
  autorun,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { Accommodation, Addon } from '@data/models';
import ReservationItem from '@data/models/ReservationItem';
import Store from '@data/stores/Store';
import { CartService } from '@data/api/services/cart';
import CartTotals from '@data/models/CartTotals';

export default class Cart {
  @observable items: ReservationItem[] = [];
  @observable totals: CartTotals | null = null;

  constructor() {
    makeObservable(this);
  }

  init() {
    autorun(async () => {
      if (this.property && this.currency) {
        await this.calculate();
      }
    });
  }

  remove(item?: ReservationItem) {
    if (!item) return;
    const index = this.items.findIndex((x) => x.id == item.id);
    const items: ReservationItem[] = [...this.items];
    items.splice(index, 1);
    this.items = items;
  }

  add(item: ReservationItem) {
    const items: ReservationItem[] = this.items || [];

    let isNew = true;
    this.items = items.map((_item) => {
      if (
        _item.getItemId() === item.getItemId() &&
        item.model?.item.packageId === _item.model?.item.packageId
      ) {
        if (
          _item.model?.type === 'accommodation' &&
          item.model?.type === 'accommodation'
        ) {
          if (_item.model?.item.isPrivate) {
            if (
              _item.model?.kids === item.model?.kids &&
              _item.model?.adults === item.model?.adults &&
              _item.model?.unit === item.model?.unit
            ) {
              isNew = false;
              return item;
            }
          }
        }
      }
      return _item;
    });

    if (isNew) {
      this.items.push(item);
    }
  }

  @action.bound addAddon(addon: Addon, amount = 1) {
    if (amount < 1) return;
    this.add(
      new ReservationItem({
        type: 'addon',
        item: addon,
        amount,
      })
    );
  }

  @action.bound addAccommodation(
    accommodation: Accommodation,
    amount: number,
    adults = 1,
    kids = 0,
    unit?: string
  ) {
    if (amount < 1) return;
    this.add(
      new ReservationItem({
        type: 'accommodation',
        item: accommodation,
        amount,
        adults,
        kids,
        unit,
      })
    );
  }

  @action.bound removeAccommodation(
    accommodation: Accommodation,
    unit?: string
  ) {
    this.remove(this.findAccommodation(accommodation, unit));
  }

  @action.bound removeAddon(item: Addon) {
    const index = this.items.findIndex((x) => x.getItemId() == item.id);
    const items: ReservationItem[] = [...this.items];
    items.splice(index, 1);
    this.items = items;
  }

  @action.bound clear() {
    this.items = [];
    this.totals = null;
  }

  findAccommodation(
    accomodation: Accommodation,
    unit?: string
  ): ReservationItem | undefined {
    return this.items.find(
      (x) =>
        x.model?.type === 'accommodation' &&
        x.model?.item.id === accomodation.id &&
        x.model?.unit === unit
    );
  }

  async calculate() {
    if (this.property && this.currency && this.checkIn && this.checkOut) {
      runInAction(() => {
        this.totals = null;
      });

      const totals = await CartService.calculate(
        this.property,
        this.items,
        this.currency,
        this.checkIn,
        this.checkOut
      );

      runInAction(() => {
        this.totals = totals;
      });
    }
  }

  @computed private get property() {
    return Store.property.model;
  }

  @computed private get currency() {
    return Store.environment.currency;
  }

  @computed get checkIn() {
    return Store.components.promoCode.value.checkin;
  }

  @computed get checkOut() {
    return Store.components.promoCode.value.checkout;
  }
}
